import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { DialogModule, TooltipModule } from "primeng/primeng";
import { PerfectScrollbarModule } from "ngx-perfect-scrollbar";

import { NgWaitModule } from "../ng-shared/ng-components/ng-wait/ng-wait.module";
import { NgPaginatorModule } from "../ng-shared/ng-components/ng-paginator/ng-paginator.module";
import { NgSharedModule } from "../ng-shared/ng-shared.module";
import { NgConnectionsService } from "./ng-connections.service";
import { TCITenantRemoteServices } from "../ng-tenant/tci-tenant-remote-services";
import { TciTenantService } from "../ng-tenant/tci-tenant.service";
import { TciTenantModelService } from "../ng-tenant/tci-tenant.model";
import { NgAppFeatureToggleService } from "../ng-shared/ng-services/ng-app-feature-toggle.service";
import { NgClickOutsideModule } from "../ng-shared/ng-directives/ng-click-outside/ng-click-outside.module";

import { NgConnectionsComponent, TCIConnectorDeleteDynLoaderComponent } from "./ng-connections.component";
import { NgConnectionsRowComponent } from "./ng-connections-row/ng-connections-row.component";
import { NgConnectionsSelectComponent } from "./ng-connections-select/ng-connections-select.component";
import { TCITenantMenuServices } from "../ng-tenant/tci-tenant.menu";
import { NgUserService } from "../ng-shared/ng-services/ng-user.service";
import { NgHamburgerMenuModule } from "../ng-app-hamburger-menu/ng-app-hamburger-menu.module";
import { DummyComponent } from "../ng-app-list/ng-app-list.module";

import { NgAuthGuardService } from "../ng-shared/ng-services/ng-auth-guard.service";
import { TciTenantConnectionsRouteActivator } from "./ng-connections.route-activator";
import { TCIRouteSelectorResolver } from "../tci-tenant-apps-route-activator";
import { TCITenantConnectionsMenu, TCI_CONNECTIONS_MENU_TYPE, TCI_COMPONENT_MODE, ITCITenantComponentOptions } from "@tci/common";
import { BaseConnectionMenuComponentInput } from "./ng-connections-menu";

function TciConnectionsTenantRouteActivatorFactory(
    tenantMenuSrv: TCITenantMenuServices,
    connsAPISrv: NgConnectionsService,
    userService: NgUserService,
    featureToggleService: NgAppFeatureToggleService,
    authSvc: NgAuthGuardService
) {
    const routeResolver: TCIRouteSelectorResolver = input => {
        const items = TCITenantConnectionsMenu
            .getAllMenuItems((v: TCITenantConnectionsMenu) => v.getOptionType() === TCI_CONNECTIONS_MENU_TYPE.HAMBURGER_MENU)
            .filter(item => item.getRouteId() === input);
        if (items && items.length > 1) {
            throw new Error("Duplicate routes found for :" + input);
        }
        return items.pop();
    };

    const inputResolver: any = (input: string, apptype, compOptions: ITCITenantComponentOptions<any>) => {
        return connsAPISrv.getConnection(input)
            .map(connData =>
                new BaseConnectionMenuComponentInput(
                    connData.tenantType,
                    compOptions.getComponentSelectorType(),
                    connData,
                    userService,
                    featureToggleService,
                    TCI_COMPONENT_MODE.COMP_MODE_CONN_HAMBURGER,
                    undefined,
                    null)
            );
    };

    return new TciTenantConnectionsRouteActivator(tenantMenuSrv, authSvc, routeResolver, inputResolver);
}

function NgConnectionsServiceFactory(
    tenantRemoteServices: TCITenantRemoteServices,
    tenantSvc: TciTenantService,
    menuSvc: TCITenantMenuServices,
    tenantModelSvc: TciTenantModelService,
    userService: NgUserService,
    featureToggleService: NgAppFeatureToggleService
): NgConnectionsService {
    return NgConnectionsService.getInstance(
        tenantRemoteServices,
        tenantSvc,
        menuSvc,
        tenantModelSvc,
        userService,
        featureToggleService
    );
}

@NgModule({
    imports: [
        CommonModule,
        ReactiveFormsModule,
        PerfectScrollbarModule,
        DialogModule,
        TooltipModule,
        NgSharedModule,
        NgWaitModule,
        NgPaginatorModule,
        NgHamburgerMenuModule,
        NgClickOutsideModule,
        RouterModule.forChild([
            {
                path: "connections",
                component: NgConnectionsComponent,
                canActivate: [
                    NgAuthGuardService
                ],
                children: [
                    {
                        path: ":routeid/:inputid",
                        component: DummyComponent,
                        canActivate: [
                            // NgAuthGuardService, // IPAS-14535 - Connections guard already includes the AuthGuard
                            TciConnectionsTenantRouteActivatorFactory
                        ],
                    },
                    {
                        path: "**",
                        redirectTo: "connections"
                    }
                ]
            }
        ])
    ],
    declarations: [
        NgConnectionsComponent,
        NgConnectionsRowComponent,
        NgConnectionsSelectComponent,
        TCIConnectorDeleteDynLoaderComponent
    ],
    providers: [
        {
            provide: NgConnectionsService,
            useFactory: NgConnectionsServiceFactory,
            deps: [
                TCITenantRemoteServices,
                TciTenantService,
                TCITenantMenuServices,
                TciTenantModelService,
                NgUserService,
                NgAppFeatureToggleService
            ]
        },
        {
            provide: TciConnectionsTenantRouteActivatorFactory,
            useFactory: TciConnectionsTenantRouteActivatorFactory,
            deps: [
                TCITenantMenuServices,
                NgConnectionsService,
                NgUserService,
                NgAppFeatureToggleService,
                NgAuthGuardService
            ]
        }
    ]
})
export class NgConnectionsModule {
    constructor(private translate: TranslateService) {
        // this language will be used as a fallback when a translation isn't found in the current language
        this.translate.setDefaultLang("en");

        // the lang to use, if the lang isn't available, it will use the current loader to get them
        this.translate.use("en");
    }
}
